<template>
  <div class="w-full">
    <PageHeader
        title="Informations BEU"
        subtitle="Bénéficiaire Effectif Ultime"
    />

    <div class="w-full md:px-6 py-6">
      <form @submit.prevent class="space-y-8">
        <!-- Section Informations Gérant et Entreprise -->
        <div class="bg-white rounded-xl shadow-lg border border-gray-200 p-4 md:p-6">
          <div class="mb-6 p-4 rounded-lg bg-blue-50 border border-blue-200 text-sm text-blue-900">
            <p class="font-semibold mb-1"><i class="fas fa-shield-alt mr-2"></i>Informations nécessaires à la vérification HiPay</p>
            <p>Complétez les informations et documents du gérant, de l’entreprise et de chaque bénéficiaire effectif. Le nom de l’entreprise indiqué sur le RIB et sur le KBIS doit être identique à celui renseigné dans le dossier ; sinon HiPay peut refuser la vérification.</p>
          </div>
          <div class="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <!-- Colonne Gérant -->
            <div class="flex flex-col h-full">
              <h3 class="text-lg font-semibold text-black mb-6 flex items-center gap-2">
                <i class="fas fa-user-tie text-blue-600"></i>
                Informations Gérant
              </h3>

              <div class="space-y-6">
                <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Nom</label>
                    <input
                        v-model="formData.manager.lastName"
                        type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Votre nom"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Prénom</label>
                    <input
                        v-model="formData.manager.firstName"
                        type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Votre prénom"
                    />
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-black mb-2">Fiche d'identité</label>
                  <div class="space-y-4">
                    <div class="beu-upload-row flex flex-wrap items-center gap-3">
                      <input
                          ref="identityCardInput"
                          type="file"
                          accept=".jpg,.jpeg,.gif,.png,.pdf"
                          @change="handleIdentityCardUpload"
                          class="hidden"
                      />
                      <button
                          type="button"
                          @click="triggerFileInput('identityCardInput')"
                          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Choisir un fichier
                      </button>
                      <div class="flex flex-col min-w-0">
                        <span class="text-sm text-black truncate max-w-[200px]">
                          {{ formData.manager.identityCard
                             ? formData.manager.identityCard.name
                             : (existingFiles.manager.identityFrontUrl ? fileNameFromUrl(existingFiles.manager.identityFrontUrl) : 'Aucun fichier choisi') }}
                        </span>
                        <span class="text-xs text-black">(jpg, jpeg, gif, png, pdf)</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <template v-if="managerPreviews.identityCard">
                          <a :href="managerPreviews.identityCard.url" target="_blank" class="text-blue-600 text-xs underline">Prévisualiser</a>
                          <img v-if="managerPreviews.identityCard.isImage" :src="managerPreviews.identityCard.url" alt="Prévisualisation" class="h-12 w-auto rounded border" />
                        </template>
                        <template v-else-if="existingFiles.manager.identityFrontUrl">
                          <a :href="existingFiles.manager.identityFrontUrl" target="_blank" class="text-blue-600 text-xs underline">Voir le fichier</a>
                        </template>
                      </div>
                    </div>

                    <div class="beu-upload-row flex flex-wrap items-center gap-3">
                      <input
                          ref="identityCardBackInput"
                          type="file"
                          accept=".jpg,.jpeg,.gif,.png,.pdf"
                          @change="handleIdentityCardBackUpload"
                          class="hidden"
                      />
                      <button
                          type="button"
                          @click="triggerFileInput('identityCardBackInput')"
                          class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                      >
                        Ajouter verso
                      </button>
                      <div class="flex flex-col min-w-0">
                        <span class="text-sm text-black truncate max-w-[200px]">
                          {{ formData.manager.identityCardBack
                             ? formData.manager.identityCardBack.name
                             : (existingFiles.manager.identityBackUrl ? fileNameFromUrl(existingFiles.manager.identityBackUrl) : 'Aucun fichier choisi') }}
                        </span>
                        <span class="text-xs text-black">(jpg, jpeg, gif, png, pdf)</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <template v-if="managerPreviews.identityCardBack">
                          <a :href="managerPreviews.identityCardBack.url" target="_blank" class="text-blue-600 text-xs underline">Prévisualiser</a>
                          <img v-if="managerPreviews.identityCardBack.isImage" :src="managerPreviews.identityCardBack.url" alt="Prévisualisation" class="h-12 w-auto rounded border" />
                        </template>
                        <template v-else-if="existingFiles.manager.identityBackUrl">
                          <a :href="existingFiles.manager.identityBackUrl" target="_blank" class="text-blue-600 text-xs underline">Voir le fichier</a>
                        </template>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div class="mt-auto pt-6 flex justify-end">
                <button
                    type="button"
                    class="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm disabled:opacity-60 inline-flex items-center gap-2"
                    :disabled="isSavingManager"
                    @click="saveManagerSection"
                >
                  <i class="fas fa-save"></i>
                  {{ isSavingManager ? 'Enregistrement…' : 'Enregistrer le gérant' }}
                </button>
              </div>
            </div>

            <!-- Colonne Entreprise -->
            <div class="flex flex-col h-full">
              <h3 class="text-lg font-semibold text-black mb-6 flex items-center gap-2">
                <i class="fas fa-building text-green-600"></i>
                Informations Entreprise
              </h3>

              <div class="space-y-6">
                <div class="grid grid-cols-1 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Numéro de TVA <span class="text-red-500">*</span></label>
                    <div class="relative">
                      <input
                          v-model="formData.company.vatNumber"
                          type="text"
                          class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          :class="vatStatusClass()"
                          placeholder="Numéro de TVA"
                      />
                      <i class="fas fa-chart-line absolute right-3 top-1/2 transform -translate-y-1/2 text-black"></i>
                    </div>

                    <p
                        v-if="!formData.company.vatNumber"
                        class="text-xs text-red-500 mt-1 flex items-center"
                    >
                      <i class="fas fa-exclamation-circle mr-1"></i>
                      Numéro de TVA obligatoire
                    </p>
                    <p
                        v-else-if="!isVatValid(formData.company.vatNumber)"
                        class="text-xs text-red-500 mt-1 flex items-center"
                    >
                      <i class="fas fa-exclamation-circle mr-1"></i>
                      Format TVA invalide. Exemple : FRXX999999999
                    </p>
                    <p
                        v-else
                        class="text-xs text-green-600 mt-1 flex items-center"
                    >
                      <i class="fas fa-check-circle mr-1"></i>
                      Format TVA valide
                    </p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">
                      IBAN
                      <span class="text-red-500">*</span>
                    </label>
                    <div class="relative">
                      <input
                          v-model="formData.company.iban"
                          type="text"
                          :maxlength="35"
                          class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          :class="ibanStatusClass()"
                          placeholder="FR76 3000 1007 9416 0000 0000 000"
                      />
                      <i class="fas fa-chart-line absolute right-3 top-1/2 transform -translate-y-1/2 text-black"></i>
                    </div>
                    <p
                        v-if="!formData.company.iban"
                        class="text-xs text-red-500 mt-1 flex items-center"
                    >
                      <i class="fas fa-exclamation-circle mr-1"></i>
                      IBAN obligatoire
                    </p>
                    <p
                        v-else-if="!isIbanValid(formData.company.iban)"
                        class="text-xs text-red-500 mt-1 flex items-center"
                    >
                      <i class="fas fa-exclamation-circle mr-1"></i>
                      Format IBAN invalide. Exemple : FR76 3000 1007 9416 0000 0000 000
                    </p>
                    <p
                        v-else
                        class="text-xs text-green-600 mt-1 flex items-center"
                    >
                      <i class="fas fa-check-circle mr-1"></i>
                      Format IBAN valide
                    </p>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-black mb-2">RIB de la société</label>
                  <div class="flex flex-wrap items-center gap-3">
                    <input
                        ref="companyRibInput"
                        type="file"
                        accept=".jpg,.jpeg,.gif,.png,.pdf"
                        @change="handleCompanyRibUpload"
                        class="hidden"
                    />
                    <button
                        type="button"
                        @click="triggerFileInput('companyRibInput')"
                        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Choisir un fichier
                    </button>
                    <div class="flex flex-col min-w-0">
                      <span class="text-sm text-black truncate max-w-[200px]">
                        {{ formData.company.companyRib
                           ? formData.company.companyRib.name
                           : (existingFiles.company.ribUrl ? fileNameFromUrl(existingFiles.company.ribUrl) : 'Aucun fichier choisi') }}
                      </span>
                      <span class="text-xs text-black">(jpg, jpeg, gif, png, pdf)</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <template v-if="companyPreviews.companyRib">
                        <a :href="companyPreviews.companyRib.url" target="_blank" class="text-blue-600 text-xs underline">Prévisualiser</a>
                        <img v-if="companyPreviews.companyRib.isImage" :src="companyPreviews.companyRib.url" alt="Prévisualisation" class="h-12 w-auto rounded border" />
                      </template>
                      <template v-else-if="existingFiles.company.ribUrl">
                        <a :href="existingFiles.company.ribUrl" target="_blank" class="text-blue-600 text-xs underline">Voir le fichier</a>
                      </template>
                    </div>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-black mb-2">Kbis datant de moins de 3 mois</label>
                  <div class="flex flex-wrap items-center gap-3">
                    <input
                        ref="kbisInput"
                        type="file"
                        accept=".jpg,.jpeg,.gif,.png,.pdf"
                        @change="handleKbisUpload"
                        class="hidden"
                    />
                    <button
                        type="button"
                        @click="triggerFileInput('kbisInput')"
                        class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      Choisir un fichier
                    </button>
                    <div class="flex flex-col min-w-0">
                      <span class="text-sm text-black truncate max-w-[200px]">
                        {{ formData.company.kbisDocument
                           ? formData.company.kbisDocument.name
                           : (existingFiles.company.kbisUrl ? fileNameFromUrl(existingFiles.company.kbisUrl) : 'Aucun fichier choisi') }}
                      </span>
                      <span class="text-xs text-black">(jpg, jpeg, gif, png, pdf)</span>
                    </div>
                    <div class="flex items-center gap-2">
                      <template v-if="companyPreviews.kbisDocument">
                        <a :href="companyPreviews.kbisDocument.url" target="_blank" class="text-blue-600 text-xs underline">Prévisualiser</a>
                        <img v-if="companyPreviews.kbisDocument.isImage" :src="companyPreviews.kbisDocument.url" alt="Prévisualisation" class="h-12 w-auto rounded border" />
                      </template>
                      <template v-else-if="existingFiles.company.kbisUrl">
                        <a :href="existingFiles.company.kbisUrl" target="_blank" class="text-blue-600 text-xs underline">Voir le fichier</a>
                      </template>
                    </div>
                  </div>
                </div>

                <div>
                  <label class="block text-sm font-medium text-black mb-2">Statuts signés avec répartition des pouvoirs</label>
                  <div class="space-y-4">
                    <div class="flex flex-wrap items-center gap-3">
                      <input
                          ref="signedArticlesInput"
                          type="file"
                          accept=".jpg,.jpeg,.gif,.png,.pdf"
                          @change="handleSignedArticlesUpload"
                          class="hidden"
                      />
                      <button
                          type="button"
                          @click="triggerFileInput('signedArticlesInput')"
                          class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                      >
                        Choisir un fichier
                      </button>
                      <div class="flex flex-col min-w-0">
                        <span class="text-sm text-black truncate max-w-[200px]">
                          {{ formData.company.signedArticles
                             ? formData.company.signedArticles.name
                             : (existingFiles.company.signedArticlesUrl ? fileNameFromUrl(existingFiles.company.signedArticlesUrl) : 'Aucun fichier choisi') }}
                        </span>
                        <span class="text-xs text-black">(jpg, jpeg, gif, png, pdf)</span>
                      </div>
                      <div class="flex items-center gap-2">
                        <template v-if="companyPreviews.signedArticles">
                          <a :href="companyPreviews.signedArticles.url" target="_blank" class="text-blue-600 text-xs underline">Prévisualiser</a>
                          <img v-if="companyPreviews.signedArticles.isImage" :src="companyPreviews.signedArticles.url" alt="Prévisualisation" class="h-12 w-auto rounded border" />
                        </template>
                        <template v-else-if="existingFiles.company.signedArticlesUrl">
                          <a :href="existingFiles.company.signedArticlesUrl" target="_blank" class="text-blue-600 text-xs underline">Voir le fichier</a>
                        </template>
                      </div>
                    </div>
                    <p class="text-xs text-black">
                      Incluant l'adresse du siège social, le nom de l'entreprise et le nom du représentant légal
                    </p>
                  </div>
                </div>
              </div>
              <div class="mt-auto pt-6 flex justify-end">
                <button
                    type="button"
                    class="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm disabled:opacity-60 inline-flex items-center gap-2"
                    :disabled="isSavingCompany"
                    @click="saveCompanySection"
                >
                  <i class="fas fa-save"></i>
                  {{ isSavingCompany ? 'Enregistrement…' : 'Enregistrer l’entreprise' }}
                </button>
              </div>
            </div>
          </div>
        </div>

        <!-- Section Informations BEU -->
        <div class="bg-white rounded-xl shadow-lg border border-gray-200 p-6">
          <div class="flex items-center justify-between mb-6">
            <h3 class="text-lg font-semibold text-black flex items-center gap-2">
              <i class="fas fa-users text-purple-600"></i>
              Informations BEU (Bénéficiaire Effectif Ultime)
            </h3>
            <p class="text-sm text-gray-600 mt-1">
              Ajoutez chaque bénéficiaire détenant au moins 25 % de l’entreprise.
            </p>
            <button
                type="button"
                @click="addBeneficiary"
                :class="[
                        'px-4 py-2 rounded-lg text-white transition-colors inline-flex items-center gap-2',
                        formData.beneficiaries.length >= 4
                          ? 'bg-gray-300 cursor-not-allowed'
                          : 'bg-green-600 hover:bg-green-700'
                        ]"
            >
              <i class="fas fa-plus"></i>
              Ajouter un bénéficiaire
            </button>
          </div>


          <div
              v-for="(beneficiary, index) in formData.beneficiaries"
              :key="beneficiary.id"
              class="mb-4 rounded-lg bg-white border border-gray-200"
          >
            <button
                type="button"
                @click="beneficiary.isOpen = !beneficiary.isOpen"
                class="w-full flex items-center justify-between px-4 py-3"
            >
              <span class="text-md font-medium text-black">Bénéficiaire {{ index + 1 }}</span>
              <span :class="['transition-transform duration-200', beneficiary.isOpen ? 'rotate-180' : '']">▼</span>
            </button>

            <transition name="accordion">
              <div v-show="beneficiary.isOpen" class="px-6 pb-6 pt-2 space-y-4">
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Civilité</label>
                    <select
                        v-model="beneficiary.salutation"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="Monsieur">Monsieur</option>
                      <option value="Madame">Madame</option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Nom</label>
                    <div class="relative">
                      <input
                          v-model="beneficiary.lastName"
                          type="text"
                          class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Nom"
                      />
                      <i class="fas fa-chart-line absolute right-3 top-1/2 transform -translate-y-1/2 text-black"></i>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Prénom</label>
                    <input
                        v-model="beneficiary.firstName"
                        type="text"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Prénom"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Numéro de carte d'identité</label>
                    <div class="relative">
                      <input
                          v-model="beneficiary.identityCardNumber"
                          type="text"
                          class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Numéro d'identité"
                      />
                      <i class="fas fa-chart-line absolute right-3 top-1/2 transform -translate-y-1/2 text-black"></i>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Adresse</label>
                    <div class="relative">
                      <input
                          v-model="beneficiary.address"
                          type="text"
                          class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Adresse"
                      />
                      <i class="fas fa-chart-line absolute right-3 top-1/2 transform -translate-y-1/2 text-black"></i>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Code postal <span class="text-red-500">*</span></label>
                    <div class="relative">
                      <input
                          v-model="beneficiary.postalCode"
                          @input="handlePostalCodeInput($event, beneficiary)"
                          type="text"
                          class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          :class="postalStatusClass(beneficiary)"
                          placeholder="Code postal"
                      />
                      <i class="fas fa-chart-line absolute right-3 top-1/2 transform -translate-y-1/2 text-black"></i>
                    </div>
                    <p
                        v-if="!beneficiary.postalCode"
                        class="text-xs text-red-500 mt-1 flex items-center"
                    >
                      <i class="fas fa-exclamation-circle mr-1"></i>
                      Code postal obligatoire
                    </p>
                    <p
                        v-else-if="!isPostalCodeValid(beneficiary.postalCode)"
                        class="text-xs text-red-500 mt-1 flex items-center"
                    >
                      <i class="fas fa-exclamation-circle mr-1"></i>
                      Code postal invalide. Exemple : 75001
                    </p>
                    <p
                        v-else
                        class="text-xs text-green-600 mt-1 flex items-center"
                    >
                      <i class="fas fa-check-circle mr-1"></i>
                      Code postal valide
                    </p>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Ville</label>
                    <div class="relative">
                      <input
                          v-model="beneficiary.city"
                          type="text"
                          class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ville"
                      />
                      <i class="fas fa-chart-line absolute right-3 top-1/2 transform -translate-y-1/2 text-black"></i>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Fonction dans l'entreprise</label>
                    <select
                        v-model="beneficiary.functionInCompany"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option value="CEO">CEO</option>
                      <option value="COO">COO</option>
                      <option value="CFO">CFO</option>
                      <option value="Actionnaire">Actionnaire</option>
                      <option value="Administrateur">Administrateur</option>
                      <option value="Autre">Autre</option>
                      <option value="Directeur d'entreprise">Directeur d'entreprise</option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Date de naissance</label>
                    <input
                        v-model="beneficiary.birthDate"
                        type="date"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Ville de naissance</label>
                    <div class="relative">
                      <input
                          v-model="beneficiary.birthCity"
                          type="text"
                          class="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="Ville de naissance"
                      />
                      <i class="fas fa-chart-line absolute right-3 top-1/2 transform -translate-y-1/2 text-black"></i>
                    </div>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Pays de naissance</label>
                    <select
                        v-model="beneficiary.birthCountry"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option v-for="c in countryOptions" :key="c.code" :value="c.code">
                        {{ c.label }}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">Nationalité</label>
                    <select
                        v-model="beneficiary.nationality"
                        class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      <option v-for="n in nationalityOptions" :key="n.code" :value="n.code">
                        {{ n.label }}
                      </option>
                    </select>
                  </div>

                  <div>
                    <label class="block text-sm font-medium text-black mb-2">
                      Part
                      <span class="text-red-500">*</span>
                    </label>
                    <input
                        v-model="beneficiary.sharePercentage"
                        @input="handleShareInput($event, beneficiary)"
                        type="text"
                        class="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        :class="shareStatusClass(beneficiary)"
                        placeholder="Pourcentage"
                    />

                    <!-- messages comme les autres champs -->
                    <p
                        v-if="!beneficiary.sharePercentage"
                        class="text-xs text-red-500 mt-1 flex items-center"
                    >
                      <i class="fas fa-exclamation-circle mr-1"></i>
                      Part obligatoire (minimum 25%).
                    </p>
                    <p
                        v-else-if="!isShareValid(beneficiary.sharePercentage)"
                        class="text-xs text-red-500 mt-1 flex items-center"
                    >
                      <i class="fas fa-exclamation-circle mr-1"></i>
                      La part doit être au moins de 25% pour le KYC.
                    </p>
                    <p
                        v-else-if="!isTotalShareValid"
                        class="text-xs text-red-500 mt-1 flex items-center"
                    >
                      <i class="fas fa-exclamation-circle mr-1"></i>
                      Total des parts: {{ totalShare }}% (le total ne doit pas dépasser 100%).
                    </p>
                    <p
                        v-else
                        class="text-xs text-green-600 mt-1 flex items-center"
                    >
                      <i class="fas fa-check-circle mr-1"></i>
                      Part valide.
                    </p>
                  </div>

                  <div class="md:col-span-2">
                    <label class="block text-sm font-medium text-black mb-2">Fiche d'identité</label>
                    <div class="space-y-3">
                      <div class="beu-upload-row flex items-center gap-3">
                        <input
                            :ref="`beneficiaryIdentityCardInput${beneficiary.id}`"
                            type="file"
                            accept=".jpg,.jpeg,.gif,.png,.pdf"
                            @change="(event) => handleBeneficiaryIdentityCardUpload(event, beneficiary.id)"
                            class="hidden"
                        />
                        <button
                            type="button"
                            @click="triggerBeneficiaryFileInput(beneficiary.id)"
                            class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                        >
                          Choisir un fichier
                        </button>
                        <span class="text-sm text-black">
                          {{ beneficiary.identityCard
                             ? beneficiary.identityCard.name
                             : (existingFiles.beneficiaries[beneficiary.id]?.frontUrl ? fileNameFromUrl(existingFiles.beneficiaries[beneficiary.id]?.frontUrl) : 'Aucun fichier choisi') }}
                        </span>
                        <span class="text-xs text-black">(jpg, jpeg, gif, png, pdf)</span>
                        <div class="flex items-center gap-2">
                          <template v-if="beneficiaryPreviews[beneficiary.id] && beneficiaryPreviews[beneficiary.id].front">
                            <a :href="beneficiaryPreviews[beneficiary.id].front.url" target="_blank" class="text-blue-600 text-xs underline">Prévisualiser</a>
                            <img v-if="beneficiaryPreviews[beneficiary.id].front.isImage" :src="beneficiaryPreviews[beneficiary.id].front.url" alt="Prévisualisation" class="h-16 w-auto rounded border" />
                          </template>
                          <template v-else-if="existingFiles.beneficiaries[beneficiary.id]?.frontUrl">
                            <a :href="existingFiles.beneficiaries[beneficiary.id].frontUrl" target="_blank" class="text-blue-600 text-xs underline">Voir le fichier</a>
                          </template>
                        </div>
                      </div>
                      <div class="beu-upload-row flex items-center gap-3">
                        <input
                            :ref="`beneficiaryIdentityCardBackInput${beneficiary.id}`"
                            type="file"
                            accept=".jpg,.jpeg,.gif,.png,.pdf"
                            @change="(event) => handleBeneficiaryIdentityCardBackUpload(event, beneficiary.id)"
                            class="hidden"
                        />
                        <button
                            type="button"
                            @click="triggerBeneficiaryBackFileInput(beneficiary.id)"
                            class="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm"
                        >
                          Ajouter verso
                        </button>
                        <span class="text-sm text-black">
                            {{ beneficiary.identityCardBack
                               ? beneficiary.identityCardBack.name
                               : (existingFiles.beneficiaries[beneficiary.id]?.backUrl ? fileNameFromUrl(existingFiles.beneficiaries[beneficiary.id]?.backUrl) : 'Aucun fichier choisi') }}
                        </span>
                        <span class="text-xs text-black">(jpg, jpeg, gif, png, pdf)</span>
                        <div class="flex items-center gap-2">
                          <template v-if="beneficiaryPreviews[beneficiary.id] && beneficiaryPreviews[beneficiary.id].back">
                            <a :href="beneficiaryPreviews[beneficiary.id].back.url" target="_blank" class="text-blue-600 text-xs underline">Prévisualiser</a>
                            <img v-if="beneficiaryPreviews[beneficiary.id].back.isImage" :src="beneficiaryPreviews[beneficiary.id].back.url" alt="Prévisualisation" class="h-16 w-auto rounded border" />
                          </template>
                          <template v-else-if="existingFiles.beneficiaries[beneficiary.id]?.backUrl">
                            <a :href="existingFiles.beneficiaries[beneficiary.id].backUrl" target="_blank" class="text-blue-600 text-xs underline">Voir le fichier</a>
                          </template>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="beu-action-buttons mt-4 flex justify-end">
                  <button
                      type="button"
                      class="beu-btn px-4 py-2 mr-3 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors text-sm inline-flex items-center gap-2"
                      @click="openSaveBeneficiaryModal(beneficiary)"
                  >
                    <i class="fas fa-save"></i>
                    Enregistrer
                  </button>
                  <button
                      type="button"
                      @click="askDeleteBeneficiary(beneficiary.id)"
                      class="beu-btn px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </transition>
          </div>
        </div>

        <!-- Footer -->
        <div class="bg-gray-50 p-6 rounded-xl border border-gray-200">
          <div class="space-y-4">
            <div class="flex items-start gap-3 p-4 bg-gray-100 rounded-lg">
              <i class="fas fa-info-circle text-black mt-1"></i>
              <p class="text-sm text-black">
                En raison des limitations du serveur, la taille maximale d'un fichier individuel est de 3 Mo, et la taille totale des fichiers ne doit pas dépasser 6 Mo. Si vos fichiers sont volumineux, vous devrez peut-être les télécharger en plusieurs fois.
              </p>
            </div>

            <div class="flex items-center gap-3">
              <input
                  v-model="formData.consentAccepted"
                  type="checkbox"
                  id="consent"
                  class="w-4 h-4 text-red-600 bg-gray-100 border-gray-300 rounded focus:ring-red-500 focus:ring-2"
              />
              <label for="consent" class="text-sm font-medium text-red-600">
                J'accepte que mes données soient utilisées uniquement pour la création d'un compte bancaire.
              </label>
            </div>

            <!-- Grossiste : un seul envoi (sauvegarde + HiPay) -->
            <div v-if="!isAdminHipayTools" class="flex justify-center">
              <button
                  type="button"
                  class="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                  :disabled="isBusy"
                  @click="openSaveFormModal('all')"
              >
                <i v-if="hipayBusyStep === 'all'" class="fas fa-spinner fa-spin"></i>
                {{ hipayBusyStep === 'all' ? 'Enregistrement et envoi HiPay…' : 'Demander la validation HiPay' }}
              </button>
            </div>

            <!-- Admin (impersonation) : envois ciblés par endpoint -->
            <template v-else>
              <p class="text-sm text-gray-600 text-center">
                Mode admin : chaque bouton enregistre puis appelle un endpoint HiPay distinct.
              </p>
              <div class="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
                <div class="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p class="font-semibold text-blue-950">Statuts HiPay de ce vendeur</p>
                    <p class="text-xs text-blue-800">La recherche utilise aussi l’e-mail si aucun account_id n’est enregistré localement.</p>
                  </div>
                  <button
                      type="button"
                      class="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors text-sm font-medium disabled:opacity-60 inline-flex items-center gap-2"
                      :disabled="isBusy || isRefreshingHipayStatus"
                      @click="refreshCurrentHipayStatus"
                  >
                    <i class="fas fa-rotate-right" :class="{ 'fa-spin': isRefreshingHipayStatus }"></i>
                    {{ isRefreshingHipayStatus ? 'Actualisation…' : 'Actualiser les statuts HiPay' }}
                  </button>
                </div>
                <div v-if="hipayVendorStatus" class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 text-sm">
                  <div class="rounded-md bg-white border border-blue-100 p-3"><span class="block text-gray-500 text-xs">Compte</span><strong>{{ hipayVendorStatus.accountStatus }}</strong><span v-if="hipayVendorStatus.accountId" class="block text-xs text-gray-500">#{{ hipayVendorStatus.accountId }}</span></div>
                  <div class="rounded-md bg-white border border-blue-100 p-3"><span class="block text-gray-500 text-xs">KYC</span><strong>{{ hipayVendorStatus.kycStatus || 'Non vérifié' }}</strong></div>
                  <div class="rounded-md bg-white border border-blue-100 p-3"><span class="block text-gray-500 text-xs">Banque</span><strong>{{ hipayVendorStatus.bankStatus }}</strong></div>
                  <div class="rounded-md bg-white border border-blue-100 p-3"><span class="block text-gray-500 text-xs">UBO</span><strong>{{ hipayVendorStatus.uboStatus || 'À compléter' }}</strong></div>
                </div>
              </div>
              <div class="flex flex-wrap justify-center gap-3">
                <button
                    type="button"
                    class="px-5 py-2.5 bg-slate-700 text-white rounded-lg hover:bg-slate-800 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    :disabled="isBusy"
                    title="PATCH /user-account (création si le compte n’existe pas)"
                    @click="openSaveFormModal('account')"
                >
                  <i v-if="hipayBusyStep === 'account'" class="fas fa-spinner fa-spin"></i>
                  Envoyer les informations du compte
                </button>
                <button
                    type="button"
                    class="px-5 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    :disabled="isBusy"
                    title="POST /identification"
                    @click="openSaveFormModal('documents')"
                >
                  <i v-if="hipayBusyStep === 'documents'" class="fas fa-spinner fa-spin"></i>
                  Envoyer documents KYC
                </button>
                <button
                    type="button"
                    class="px-5 py-2.5 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    :disabled="isBusy"
                    title="POST /user-account/bank-info"
                    @click="openSaveFormModal('bank')"
                >
                  <i v-if="hipayBusyStep === 'bank'" class="fas fa-spinner fa-spin"></i>
                  Envoyer le RIB
                </button>
                <button
                    type="button"
                    class="px-5 py-2.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    :disabled="isBusy"
                    title="POST /ubo/ubo-statement + POST /ubo"
                    @click="openSaveFormModal('ubo')"
                >
                  <i v-if="hipayBusyStep === 'ubo'" class="fas fa-spinner fa-spin"></i>
                  Envoyer les UBO
                </button>
                <button
                    type="button"
                    class="px-5 py-2.5 bg-gray-700 text-white rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    :disabled="isBusy || isLoadingUboStatement"
                    title="GET /ubo/ubo-statement — lecture seule"
                    @click="loadRawUboStatement"
                >
                  <i class="fas fa-eye" :class="{ 'fa-spin fa-spinner': isLoadingUboStatement }"></i>
                  {{ isLoadingUboStatement ? 'Lecture…' : 'Voir le GET UBO' }}
                </button>
                <button
                    v-if="uboRecreateRequired"
                    type="button"
                    class="px-5 py-2.5 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    :disabled="isBusy"
                    title="Recréation explicite après incohérence entre HiPay et l’historique local"
                    @click="openSaveFormModal('ubo_recreate')"
                >
                  <i v-if="hipayBusyStep === 'ubo_recreate'" class="fas fa-spinner fa-spin"></i>
                  Confirmer la recréation UBO
                </button>
                <button
                    type="button"
                    class="px-5 py-2.5 bg-red-700 text-white rounded-lg hover:bg-red-800 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    :disabled="isBusy"
                    title="DELETE /ubo/ubo-statement — impossible si WAITING ou UPDATED"
                    @click="openDeleteUboStatementModal"
                >
                  <i v-if="isDeletingUboStatement" class="fas fa-spinner fa-spin"></i>
                  Supprimer le UBO statement
                </button>
                <button
                    type="button"
                    class="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-60 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    :disabled="isBusy"
                    title="Enchaîne identification + bank-info + UBO"
                    @click="openSaveFormModal('all')"
                >
                  <i v-if="hipayBusyStep === 'all'" class="fas fa-spinner fa-spin"></i>
                  Tout envoyer à HiPay
                </button>
              </div>
              <div v-if="rawUboStatement" class="rounded-lg border border-gray-300 bg-gray-950 p-4 text-left">
                <div class="flex flex-wrap items-center justify-between gap-2 mb-3 text-xs text-gray-300">
                  <span>{{ rawUboStatement.email }}</span>
                  <span>account_id utilisé : {{ rawUboStatement.accountId }}</span>
                </div>
                <pre class="max-h-96 overflow-auto whitespace-pre-wrap break-words text-xs text-green-300">{{ rawUboStatement.payload }}</pre>
              </div>
            </template>
          </div>
        </div>

        <!-- Modal suppression bénéficiaire -->
        <ModalComp
            :is-visible="showDeleteModal"
            title="Supprimer le bénéficiaire ?"
            :content="deleteModalContent"
            cancel-text="Annuler"
            confirm-text="Supprimer"
            @confirm="confirmDeleteBeneficiary"
            @update:is-visible="showDeleteModal = $event"
        />

        <!-- Modal enregistrement d'un bénéficiaire -->
        <ModalComp
            :is-visible="showSaveBeneficiaryModal"
            title="Enregistrer le bénéficiaire"
            :content="saveBeneficiaryContent"
            cancel-text="Annuler"
            confirm-text="Enregistrer"
            @confirm="confirmSaveBeneficiary"
            @update:is-visible="showSaveBeneficiaryModal = $event"
        />

        <!-- Modal enregistrement / envoi HiPay -->
        <ModalComp
            :is-visible="showSaveFormModal"
            :title="saveFormModalTitle"
            :content="saveFormModalContent"
            cancel-text="Annuler"
            :confirm-text="saveFormModalConfirm"
            @confirm="confirmSaveForm"
            @update:is-visible="showSaveFormModal = $event"
        />

        <!-- Modal suppression déclaration UBO HiPay -->
        <ModalComp
            :is-visible="showDeleteUboStatementModal"
            title="Supprimer le UBO statement ?"
            content="Appeler DELETE /ubo/ubo-statement chez HiPay ? Impossible si le statut est WAITING ou UPDATED. Les flags locaux UBO seront réinitialisés."
            cancel-text="Annuler"
            confirm-text="Supprimer le statement"
            @confirm="confirmDeleteUboStatement"
            @update:is-visible="showDeleteUboStatementModal = $event"
        />

      </form>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted,watch,computed } from 'vue'
import { PageHeader, ModalComp } from '@/components'
import { useNotification } from '@/composables/useNotification'
import type { BankFormData, BeneficiaryInfo } from '@/types'
import { getVendeurHipayBeu, saveVendeurHipayBeu,deleteVendeurHipayBeneficiary,saveVendeurHipayBeneficiary, getHipayFiles, submitVendeurHipayAccount, deleteVendeurHipayUboStatement, type HipaySubmitStep } from '@/api/vendeur'
import { uploadHipayFile } from '@/api/vendeur'
import {useRouter} from "vue-router";
import {useAuthStore} from "@/stores/auth";
import { fetchAdminHipayUboStatement, refreshAdminHipayVendor, type AdminHipayUboStatementResult, type AdminHipayVendor } from '@/api/adminHipay'


const countryOptions = [
  { code: 'CN', label: 'Chine' },
  { code: 'FR', label: 'France' },
  { code: 'US', label: 'États-Unis' },
  { code: 'DE', label: 'Allemagne' },
]

const nationalityOptions = [
  { code: 'CN', label: 'Chinois' },
  { code: 'FR', label: 'Français' },
  { code: 'US', label: 'Américain' },
  { code: 'DE', label: 'Allemand' },
]

// État réactif
const formData = reactive<BankFormData>({
  manager: {
    lastName: '',
    firstName: '',
    identityCard: null,
    identityCardBack: null
  },
  company: {
    vatNumber: '',
    iban: '',
    companyRib: null,
    kbisDocument: null,
    signedArticles: null
  },
  beneficiaries: [
    {
      id: 1,
      salutation: 'Monsieur',
      lastName: '',
      firstName: '',
      identityCardNumber: '',
      address: '',
      postalCode: '',
      city: '',
      functionInCompany: 'CEO',
      birthDate: '',
      birthCity: '',
      birthCountry: 'Chine',
      nationality: 'Chinois',
      sharePercentage: '',
      identityCard: null,
      identityCardBack: null
    }
  ],
  consentAccepted: false
})

let nextBeneficiaryId = 2
const MAX_BENEFICIARIES = 4

// Refs pour les inputs de fichiers
const identityCardInput = ref<HTMLInputElement>()
const identityCardBackInput = ref<HTMLInputElement>()
const companyRibInput = ref<HTMLInputElement>()
const kbisInput = ref<HTMLInputElement>()
const signedArticlesInput = ref<HTMLInputElement>()

const showDeleteModal = ref(false)
const beneficiaryToDelete = ref<number | null>(null)
const showSaveBeneficiaryModal = ref(false)
const showSaveFormModal = ref(false)
const showDeleteUboStatementModal = ref(false)
const isSavingForm = ref(false)
const isSavingManager = ref(false)
const isSavingCompany = ref(false)
const beneficiaryToSave = ref<any | null>(null)
type BeuActionStep = HipaySubmitStep
const pendingFormStep = ref<BeuActionStep>('all')
const hipayBusyStep = ref<BeuActionStep | null>(null)
const isDeletingUboStatement = ref(false)
const uboRecreateRequired = ref(false)
const isRefreshingHipayStatus = ref(false)
const hipayVendorStatus = ref<AdminHipayVendor | null>(null)
const isLoadingUboStatement = ref(false)
const rawUboStatement = ref<AdminHipayUboStatementResult | null>(null)
const isBusy = computed(() => isSavingForm.value || !!hipayBusyStep.value || isDeletingUboStatement.value)
const { success, error: notifyError } = useNotification()

const router = useRouter()
const authStore = useAuthStore()
const isAdminHipayTools = computed(() => authStore.isImpersonating)

const refreshCurrentHipayStatus = async () => {
  const idVendeur = Number(authStore.user?.id_vendeur ?? authStore.user?.id)
  if (!idVendeur || isRefreshingHipayStatus.value) return
  isRefreshingHipayStatus.value = true
  try {
    hipayVendorStatus.value = await refreshAdminHipayVendor(idVendeur)
    success(
      hipayVendorStatus.value.accountId
        ? 'Statuts HiPay actualisés pour ce vendeur.'
        : `Aucun compte HiPay trouvé pour ${hipayVendorStatus.value.email || 'cet e-mail'}.`,
    )
  } catch (e) {
    notifyError(e instanceof Error ? e.message : 'Impossible d’actualiser les statuts HiPay.')
  } finally {
    isRefreshingHipayStatus.value = false
  }
}

const loadRawUboStatement = async () => {
  const idVendeur = Number(authStore.user?.id_vendeur ?? authStore.user?.id)
  if (!idVendeur || isLoadingUboStatement.value) return
  isLoadingUboStatement.value = true
  try {
    rawUboStatement.value = await fetchAdminHipayUboStatement(idVendeur)
  } catch (e) {
    rawUboStatement.value = null
    notifyError(e instanceof Error ? e.message : 'Impossible de lire le GET UBO HiPay.')
  } finally {
    isLoadingUboStatement.value = false
  }
}

const saveFormModalTitle = computed(() => {
  switch (pendingFormStep.value) {
    case 'account': return 'Envoyer les informations du compte'
    case 'documents': return 'Envoyer les documents KYC'
    case 'bank': return 'Envoyer le RIB'
    case 'ubo': return 'Envoyer les UBO'
    case 'ubo_recreate': return 'Confirmer la recréation de la déclaration UBO'
    default: return 'Demander la validation HiPay'
  }
})
const saveFormModalContent = computed(() => {
  switch (pendingFormStep.value) {
    case 'account':
      return 'Enregistrer puis mettre à jour le compte marchand HiPay (PATCH /user-account), ou le créer s’il n’existe pas ?'
    case 'documents':
      return 'Enregistrer puis envoyer les documents KYC à HiPay (POST /identification) ?'
    case 'bank':
      return 'Enregistrer puis envoyer IBAN + RIB à HiPay (POST /user-account/bank-info) ?'
    case 'ubo':
      return 'Enregistrer puis envoyer les bénéficiaires à HiPay (POST /ubo/ubo-statement puis POST /ubo) ?'
    case 'ubo_recreate':
      return 'HiPay ne retrouve aucune déclaration alors que notre historique indique un ancien envoi. Confirmez-vous la création d’une nouvelle déclaration UBO et le renvoi des bénéficiaires ? Cette action peut créer des doublons si le mauvais compte HiPay est consulté.'
    default:
      return 'Enregistrer les informations puis les envoyer à HiPay pour validation ?'
  }
})
const saveFormModalConfirm = computed(() => {
  switch (pendingFormStep.value) {
    case 'account': return 'Envoyer le compte'
    case 'documents': return 'Envoyer documents'
    case 'bank': return 'Envoyer RIB'
    case 'ubo': return 'Envoyer UBO'
    case 'ubo_recreate': return 'Confirmer et recréer'
    default: return 'Envoyer à HiPay'
  }
})


// ------- File preview helpers -------
type PreviewEntry = { url: string; isImage: boolean }

const isImageFile = (file: File | null | undefined) => {
  if (!file) return false
  return file.type.startsWith('image/')
}

const makePreview = (file: File): PreviewEntry => ({
  url: URL.createObjectURL(file),
  isImage: isImageFile(file),
})

const revokePreview = (entry?: PreviewEntry) => {
  try { if (entry?.url) URL.revokeObjectURL(entry.url) } catch (e) { /* ignore revoke errors */ }
}

const managerPreviews = reactive<{ identityCard?: PreviewEntry; identityCardBack?: PreviewEntry }>({})
const companyPreviews = reactive<{ companyRib?: PreviewEntry; kbisDocument?: PreviewEntry; signedArticles?: PreviewEntry }>({})
const beneficiaryPreviews = reactive<Record<number, { front?: PreviewEntry; back?: PreviewEntry }>>({})

// ------- Server existing files (for echo after refresh) -------
type ExistingFiles = {
  manager: { identityFrontUrl?: string; identityBackUrl?: string }
  company: { ribUrl?: string; kbisUrl?: string; signedArticlesUrl?: string }
  beneficiaries: Record<number, { frontUrl?: string; backUrl?: string }>
}
const existingFiles = reactive<ExistingFiles>({
  manager: {},
  company: {},
  beneficiaries: {}
})

const fileNameFromUrl = (url?: string) => {
  if (!url) return ''
  const parts = url.split(/[/\\]/)
  return parts[parts.length - 1] || url
}


const deleteModalContent = computed(() => {
  if (beneficiaryToDelete.value === null) return ''
  const b = formData.beneficiaries.find(x => x.id === beneficiaryToDelete.value)
  const name = b ? `${b.lastName || ''} ${b.firstName || ''}`.trim() : ''
  return name
      ? `Êtes-vous sûr de vouloir supprimer le bénéficiaire "${name}" du formulaire ?`
      : 'Êtes-vous sûr de vouloir supprimer ce bénéficiaire du formulaire ?'
})

const saveBeneficiaryContent = computed(() => {
  if (!beneficiaryToSave.value) return ''
  const b = beneficiaryToSave.value
  const name = `${b.lastName || ''} ${b.firstName || ''}`.trim() || 'ce bénéficiaire'
  return `Enregistrer les informations pour "${name}" ?`
})
const loadHipayBeu = async () => {
  try {
    const data = await getVendeurHipayBeu()
    if (!data) return

    // manager
    formData.manager.lastName = data.manager?.lastName || ''
    formData.manager.firstName = data.manager?.firstName || ''

    // company
    formData.company.vatNumber = data.company?.vatNumber || ''
    formData.company.iban = data.company?.iban || ''

    // beneficiaries
    if (Array.isArray(data.beneficiaries) && data.beneficiaries.length > 0) {
      formData.beneficiaries = data.beneficiaries.map((b: any, idx: number) => ({
        id: b.id,
        salutation: b.salutation || 'Monsieur',
        lastName: b.lastName || '',
        firstName: b.firstName || '',
        identityCardNumber: b.identityCardNumber || '',
        address: b.address || '',
        postalCode: b.postalCode || '',
        city: b.city || '',
        functionInCompany: b.functionInCompany || 'Autre',
        birthDate: b.birthDate || '',
        birthCity: b.birthCity || '',
        birthCountry: b.birthCountry || 'CN',
        nationality: b.nationality || 'CN',
        sharePercentage: b.sharePercentage || '',
        identityCard: null,
        identityCardBack: null,
        isOpen: idx === 0,
      }))
      nextBeneficiaryId = Math.max(
          ...formData.beneficiaries.map(b => Number(b.id) || 0)
      ) + 1
    }
  }catch (e) {
    // Erreur silencieuse lors du chargement des données BEU
  }
}

// Fonction pour déclencher les inputs de fichiers
const triggerFileInput = (inputName: string) => {
  switch (inputName) {
    case 'identityCardInput':
      identityCardInput.value?.click()
      break
    case 'identityCardBackInput':
      identityCardBackInput.value?.click()
      break
    case 'companyRibInput':
      companyRibInput.value?.click()
      break
    case 'kbisInput':
      kbisInput.value?.click()
      break
    case 'signedArticlesInput':
      signedArticlesInput.value?.click()
      break
  }
}

// Fonction pour déclencher les inputs de fichiers des bénéficiaires
const triggerBeneficiaryFileInput = (beneficiaryId: number) => {
  // Pour simplifier, on utilise un input caché global
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.jpg,.jpeg,.gif,.png,.pdf'
  input.onchange = (event) => {
    const target = event.target as HTMLInputElement
    if (target.files && target.files[0]) {
      const file = target.files[0]
      const beneficiary = formData.beneficiaries.find(
          b => Number(b.id) === Number(beneficiaryId)
      )
      if (beneficiary) {
        beneficiary.identityCard = file
        // previews
        if (!beneficiaryPreviews[beneficiaryId]) beneficiaryPreviews[beneficiaryId] = {}
        revokePreview(beneficiaryPreviews[beneficiaryId].front)
        beneficiaryPreviews[beneficiaryId].front = makePreview(file)
      }
    }
  }
  input.click()
}

const triggerBeneficiaryBackFileInput = (beneficiaryId: number) => {
  const input = document.createElement('input')
  input.type = 'file'
  input.accept = '.jpg,.jpeg,.gif,.png,.pdf'
  input.onchange = (event) => {
    const target = event.target as HTMLInputElement
    if (target.files && target.files[0]) {
      const file = target.files[0]
      const beneficiary = formData.beneficiaries.find(b => b.id === beneficiaryId)
      if (beneficiary) {
        beneficiary.identityCardBack = file
        if (!beneficiaryPreviews[beneficiaryId]) beneficiaryPreviews[beneficiaryId] = {}
        revokePreview(beneficiaryPreviews[beneficiaryId].back)
        beneficiaryPreviews[beneficiaryId].back = makePreview(file)
      }
    }
  }
  input.click()
}

// Fonctions pour gérer les uploads de fichiers
const handleIdentityCardUpload = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    formData.manager.identityCard = target.files[0]
    revokePreview(managerPreviews.identityCard)
    managerPreviews.identityCard = makePreview(target.files[0])
  }
}

const handleIdentityCardBackUpload = (event: Event) => {
  // Logique pour ajouter le verso
  //console.log('Ajouter verso de la carte d\'identité du gérant')
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    formData.manager.identityCardBack = target.files[0]
    revokePreview(managerPreviews.identityCardBack)
    managerPreviews.identityCardBack = makePreview(target.files[0])
  }
}

const handleCompanyRibUpload = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    formData.company.companyRib = target.files[0]
    revokePreview(companyPreviews.companyRib)
    companyPreviews.companyRib = makePreview(target.files[0])
  }
}

const handleKbisUpload = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    formData.company.kbisDocument = target.files[0]
    revokePreview(companyPreviews.kbisDocument)
    companyPreviews.kbisDocument = makePreview(target.files[0])
  }
}

const handleSignedArticlesUpload = (event: Event) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    formData.company.signedArticles = target.files[0]
    revokePreview(companyPreviews.signedArticles)
    companyPreviews.signedArticles = makePreview(target.files[0])
  }
}

const handleBeneficiaryIdentityCardUpload = (event: Event, beneficiaryId: number) => {
  const target = event.target as HTMLInputElement
  if (target.files && target.files[0]) {
    const beneficiary = formData.beneficiaries.find(
        b => Number(b.id) === Number(beneficiaryId)
    )
    if (beneficiary) {
      beneficiary.identityCard = target.files[0]
      if (!beneficiaryPreviews[beneficiaryId]) beneficiaryPreviews[beneficiaryId] = {}
      revokePreview(beneficiaryPreviews[beneficiaryId].front)
      beneficiaryPreviews[beneficiaryId].front = makePreview(target.files[0])
    }
  }
}

const handleBeneficiaryIdentityCardBackUpload = (event: Event, beneficiaryId: number) => {
  const target = event.target as HTMLInputElement

  if (target.files && target.files[0]) {
    const file = target.files[0]

    const beneficiary = formData.beneficiaries.find(
        (b) => b.id === beneficiaryId
    )
    if (beneficiary) {

      beneficiary.identityCardBack = file
      if (!beneficiaryPreviews[beneficiaryId]) beneficiaryPreviews[beneficiaryId] = {}
      revokePreview(beneficiaryPreviews[beneficiaryId].back)
      beneficiaryPreviews[beneficiaryId].back = makePreview(file)
    }
  }
}

const handleShareInput = (event: Event, beneficiary: any) => {
  const target = event.target as HTMLInputElement
  const clean = target.value.replace(/[^\d.,]/g, '')
  beneficiary.sharePercentage = clean
}
// Fonctions pour gérer les bénéficiaires
const addBeneficiary = () => {
  if (formData.beneficiaries.length >= MAX_BENEFICIARIES) {
    notifyError(`Vous ne pouvez pas ajouter plus de ${MAX_BENEFICIARIES} bénéficiaires.`)
    return
  }

  formData.beneficiaries.forEach(b => (b.isOpen = false))
  const newBeneficiary: BeneficiaryInfo & { isOpen: boolean } = {
    id: nextBeneficiaryId++,
    salutation: 'Monsieur',
    lastName: '',
    firstName: '',
    identityCardNumber: '',
    address: '',
    postalCode: '',
    city: '',
    functionInCompany: 'CEO',
    birthDate: '',
    birthCity: '',
    birthCountry: 'Chine',
    nationality: 'Chinois',
    sharePercentage: '',
    identityCard: null,
    identityCardBack: null,
    isOpen: true
  }
  formData.beneficiaries.push(newBeneficiary)
}

const askDeleteBeneficiary = (id: number) => {
  beneficiaryToDelete.value = id
  showDeleteModal.value = true
}

const confirmDeleteBeneficiary = async () => {
  const id = beneficiaryToDelete.value
  if (id !== null) {
    try {
      await deleteVendeurHipayBeneficiary(id)

      const index = formData.beneficiaries.findIndex(b => b.id === id)
      if (index > -1) {
        formData.beneficiaries.splice(index, 1)
        success('Bénéficiaire supprimé avec succès !')
      }
    } catch (e) {
    notifyError('Erreur lors de la suppression du bénéficiaire')
    }
  }

  showDeleteModal.value = false
  beneficiaryToDelete.value = null
}

const cancelDeleteBeneficiary = () => {
  showDeleteModal.value = false
  beneficiaryToDelete.value = null
}

const refreshExistingFiles = async () => {
  const files = await getHipayFiles()
  existingFiles.manager.identityFrontUrl = files?.manager?.identityFront || undefined
  existingFiles.manager.identityBackUrl = files?.manager?.identityBack || undefined
  existingFiles.company.ribUrl = files?.company?.rib || undefined
  existingFiles.company.kbisUrl = files?.company?.kbis || undefined
  existingFiles.company.signedArticlesUrl = files?.company?.signedArticles || undefined
  existingFiles.beneficiaries = {}
  for (const key of Object.keys(files?.beneficiaries || {})) {
    const id = Number(key)
    existingFiles.beneficiaries[id] = {
      frontUrl: files.beneficiaries[key]?.front || undefined,
      backUrl: files.beneficiaries[key]?.back || undefined,
    }
  }
}

const saveManagerSection = async () => {
  if (isSavingManager.value) return
  isSavingManager.value = true

  try {
    await saveVendeurHipayBeu({
      manager: {
        lastName: formData.manager.lastName || null,
        firstName: formData.manager.firstName || null,
      },
    })
    if (formData.manager.identityCard) {
      await uploadHipayFile(formData.manager.identityCard, 'manager_identity_front')
    }
    if (formData.manager.identityCardBack) {
      await uploadHipayFile(formData.manager.identityCardBack, 'manager_identity_back')
    }
    formData.manager.identityCard = null
    formData.manager.identityCardBack = null
    if (identityCardInput.value) identityCardInput.value.value = ''
    if (identityCardBackInput.value) identityCardBackInput.value.value = ''
    revokePreview(managerPreviews.identityCard); managerPreviews.identityCard = undefined
    revokePreview(managerPreviews.identityCardBack); managerPreviews.identityCardBack = undefined
    await refreshExistingFiles()
    success('Informations du gérant enregistrées.')
  } catch (e) {
    notifyError(e instanceof Error ? e.message : "Erreur lors de l'enregistrement du gérant.")
  } finally {
    isSavingManager.value = false
  }
}

const saveCompanySection = async () => {
  if (isSavingCompany.value) return
  isSavingCompany.value = true

  try {
    await saveVendeurHipayBeu({
      company: {
        vatNumber: formData.company.vatNumber || null,
        iban: formData.company.iban || null,
      },
    })
    if (formData.company.companyRib) {
      await uploadHipayFile(formData.company.companyRib, 'company_rib')
    }
    if (formData.company.kbisDocument) {
      await uploadHipayFile(formData.company.kbisDocument, 'company_kbis')
    }
    if (formData.company.signedArticles) {
      await uploadHipayFile(formData.company.signedArticles, 'company_signed_articles')
    }
    formData.company.companyRib = null
    formData.company.kbisDocument = null
    formData.company.signedArticles = null
    if (companyRibInput.value) companyRibInput.value.value = ''
    if (kbisInput.value) kbisInput.value.value = ''
    if (signedArticlesInput.value) signedArticlesInput.value.value = ''
    revokePreview(companyPreviews.companyRib); companyPreviews.companyRib = undefined
    revokePreview(companyPreviews.kbisDocument); companyPreviews.kbisDocument = undefined
    revokePreview(companyPreviews.signedArticles); companyPreviews.signedArticles = undefined
    await refreshExistingFiles()
    success("Informations de l’entreprise enregistrées.")
  } catch (e) {
    notifyError(e instanceof Error ? e.message : "Erreur lors de l'enregistrement de l’entreprise.")
  } finally {
    isSavingCompany.value = false
  }
}
// const removeBeneficiary = (beneficiaryId: number) => {
//   const index = formData.beneficiaries.findIndex(b => b.id === beneficiaryId)
//   if (index > -1) {
//     formData.beneficiaries.splice(index, 1)
//   }
// }

// Fonction pour sauvegarder le formulaire (+ envoi HiPay optionnel par étape)
const saveForm = async (step: BeuActionStep = pendingFormStep.value) => {
  if (isSavingForm.value) return

  try {
    if (!formData.consentAccepted) {
      notifyError("Vous devez accepter les conditions pour continuer.")
      return
    }

    const needDocs = step === 'all' || step === 'documents'
    const needBank = step === 'all' || step === 'bank'
    const needUbos = step === 'all' || step === 'ubo' || step === 'ubo_recreate'

    if (needUbos && !isTotalShareValid.value) {
      notifyError('Le total des parts des bénéficiaires ne doit pas dépasser 100%.')
      return
    }

    const hasManagerIdFront = !!formData.manager.identityCard || !!existingFiles.manager.identityFrontUrl
    if (needDocs && !hasManagerIdFront) {
      notifyError("La fiche d'identité du gérant est obligatoire pour enregistrer.")
      return
    }

    const hasCompanyRib = !!formData.company.companyRib || !!existingFiles.company.ribUrl
    const hasCompanyKbis = !!formData.company.kbisDocument || !!existingFiles.company.kbisUrl
    const hasSignedArticles = !!formData.company.signedArticles || !!existingFiles.company.signedArticlesUrl
    if (needBank && !hasCompanyRib) {
      notifyError('Le RIB est obligatoire pour cette étape.')
      return
    }
    if (needDocs && (!hasCompanyKbis || !hasSignedArticles)) {
      notifyError('Le KBIS et les statuts signés sont obligatoires pour cette étape.')
      return
    }
    if (needUbos && !hasCompanyKbis) {
      notifyError('Le KBIS est obligatoire pour envoyer les bénéficiaires.')
      return
    }
    if ((step === 'all') && (!hasCompanyRib || !hasCompanyKbis || !hasSignedArticles)) {
      notifyError('Le RIB, le KBIS et les statuts signés sont obligatoires pour enregistrer.')
      return
    }

    if (needUbos) {
      for (const b of formData.beneficiaries) {
        if (!isShareValid(b.sharePercentage)) {
          notifyError("La part de chaque bénéficiaire doit être au moins de 25% pour le KYC.")
          return
        }
        const hasBenFront = !!b.identityCard || !!existingFiles.beneficiaries[Number(b.id)]?.frontUrl
        if (!hasBenFront) {
          notifyError("La fiche d'identité de chaque bénéficiaire est obligatoire pour enregistrer.")
          return
        }
      }
    }

    const beneficiaryFiles = formData.beneficiaries.map(b => ({
      tempId: b.id,
      identityCard: b.identityCard,
      identityCardBack: b.identityCardBack,
    }))

    const payload = {
      manager: {
        lastName: formData.manager.lastName || null,
        firstName: formData.manager.firstName || null,
      },
      company: {
        vatNumber: formData.company.vatNumber || null,
        iban: formData.company.iban || null,
      },
      beneficiaries: formData.beneficiaries.map(b => ({
        id: typeof b.id === 'number' ? b.id : null,
        salutation: b.salutation || null,
        lastName: b.lastName || null,
        firstName: b.firstName || null,
        identityCardNumber: b.identityCardNumber || null,
        address: b.address || null,
        postalCode: b.postalCode || null,
        city: b.city || null,
        functionInCompany: b.functionInCompany || null,
        birthDate: b.birthDate || null,
        birthCity: b.birthCity || null,
        birthCountry: b.birthCountry || null,
        nationality: b.nationality || null,
        sharePercentage: b.sharePercentage || null,
      })),
    }

    isSavingForm.value = true
    hipayBusyStep.value = step

    const saved = await saveVendeurHipayBeu(payload)

    if (saved) {
      formData.manager.lastName = saved.manager?.lastName || ''
      formData.manager.firstName = saved.manager?.firstName || ''

      formData.company.vatNumber = saved.company?.vatNumber || ''
      formData.company.iban = saved.company?.iban || ''


      if (Array.isArray(saved.beneficiaries)) {
        formData.beneficiaries = saved.beneficiaries.map((b: any, idx: number) => {
          const files =
              beneficiaryFiles.find(f => Number(f.tempId) === Number(b.id)) ||
              beneficiaryFiles[idx]

          return {
            id: b.id ?? idx + 1,
            salutation: b.salutation || 'Monsieur',
            lastName: b.lastName || '',
            firstName: b.firstName || '',
            identityCardNumber: b.identityCardNumber || '',
            address: b.address || '',
            postalCode: b.postalCode || '',
            city: b.city || '',
            functionInCompany: b.functionInCompany || 'Autre',
            birthDate: b.birthDate || '',
            birthCity: b.birthCity || '',
            birthCountry: b.birthCountry || 'CN',
            nationality: b.nationality || 'CN',
            sharePercentage: b.sharePercentage || '',
            identityCard: files?.identityCard ?? null,
            identityCardBack: files?.identityCardBack ?? null,
            isOpen: idx === saved.beneficiaries.length - 1,
          }
        })
      }

      // （manager + company + beneficiaries）
      // --- gérant ---
      if (formData.manager.identityCard) {
        await uploadHipayFile(formData.manager.identityCard, 'manager_identity_front')
      }
      if (formData.manager.identityCardBack) {
        await uploadHipayFile(formData.manager.identityCardBack, 'manager_identity_back')
      }

      // --- company ---
      if (formData.company.companyRib) {
        await uploadHipayFile(formData.company.companyRib, 'company_rib')
      }
      if (formData.company.kbisDocument) {
        await uploadHipayFile(formData.company.kbisDocument, 'company_kbis')
      }
      if (formData.company.signedArticles) {
        await uploadHipayFile(formData.company.signedArticles, 'company_signed_articles')
      }

      // --- beneficiaries ---
      for (const b of formData.beneficiaries) {
        if (b.identityCard) {
          await uploadHipayFile(b.identityCard, 'beneficiary_identity_front', {
            beneficiaryId: Number(b.id),
          })
        }
        if (b.identityCardBack) {
          await uploadHipayFile(b.identityCardBack, 'beneficiary_identity_back', {
            beneficiaryId: Number(b.id),
          })
        }
      }


      formData.manager.lastName = saved.manager?.lastName || ''
      formData.manager.firstName = saved.manager?.firstName || ''

      formData.company.vatNumber = saved.company?.vatNumber || ''
      formData.company.iban = saved.company?.iban || ''

      if (Array.isArray(saved.beneficiaries)) {
        formData.beneficiaries = saved.beneficiaries.map((b: any, idx: number) => ({
          id: b.id ?? idx + 1,
          salutation: b.salutation || 'Monsieur',
          lastName: b.lastName || '',
          firstName: b.firstName || '',
          identityCardNumber: b.identityCardNumber || '',
          address: b.address || '',
          postalCode: b.postalCode || '',
          city: b.city || '',
          functionInCompany: b.functionInCompany || 'Autre',
          birthDate: b.birthDate || '',
          birthCity: b.birthCity || '',
          birthCountry: b.birthCountry || 'Chine',
          nationality: b.nationality || 'Chinois',
          sharePercentage: b.sharePercentage || '',
          identityCard: null,
          identityCardBack: null,
          isOpen: idx === saved.beneficiaries.length - 1,
        }))
      }

      // 4. clear <input type="file">
      if (identityCardInput.value) identityCardInput.value.value = ''
      if (identityCardBackInput.value) identityCardBackInput.value.value = ''
      if (companyRibInput.value) companyRibInput.value.value = ''
      if (kbisInput.value) kbisInput.value.value = ''
      if (signedArticlesInput.value) signedArticlesInput.value.value = ''

      // Clear previews as files were uploaded and inputs reset
      revokePreview(managerPreviews.identityCard); managerPreviews.identityCard = undefined
      revokePreview(managerPreviews.identityCardBack); managerPreviews.identityCardBack = undefined
      revokePreview(companyPreviews.companyRib); companyPreviews.companyRib = undefined
      revokePreview(companyPreviews.kbisDocument); companyPreviews.kbisDocument = undefined
      revokePreview(companyPreviews.signedArticles); companyPreviews.signedArticles = undefined
      for (const key of Object.keys(beneficiaryPreviews)) {
        const id = Number(key)
        revokePreview(beneficiaryPreviews[id]?.front); if (beneficiaryPreviews[id]) beneficiaryPreviews[id].front = undefined
        revokePreview(beneficiaryPreviews[id]?.back); if (beneficiaryPreviews[id]) beneficiaryPreviews[id].back = undefined
      }


      // Recharger les fichiers existants après sauvegarde réussie
      try {
        const files = await getHipayFiles()
        // Mapper vers existingFiles structure
        existingFiles.manager.identityFrontUrl = files?.manager?.identityFront || undefined
        existingFiles.manager.identityBackUrl = files?.manager?.identityBack || undefined
        existingFiles.company.ribUrl = files?.company?.rib || undefined
        existingFiles.company.kbisUrl = files?.company?.kbis || undefined
        existingFiles.company.signedArticlesUrl = files?.company?.signedArticles || undefined
        existingFiles.beneficiaries = {}
        if (files?.beneficiaries) {
          for (const key of Object.keys(files.beneficiaries)) {
            const id = Number(key)
            existingFiles.beneficiaries[id] = {
              frontUrl: files.beneficiaries[key]?.front || undefined,
              backUrl: files.beneficiaries[key]?.back || undefined,
            }
          }
        }
      } catch (e) {
        // Erreur silencieuse lors de la récupération des fichiers après sauvegarde
        console.warn('Erreur lors de la récupération des fichiers après sauvegarde:', e)
      }

      try {
          const hipayResult = await submitVendeurHipayAccount(step)
          if (step === 'ubo' || step === 'ubo_recreate') {
            uboRecreateRequired.value = false
          }
          success(hipayResult?.message || 'Envoi HiPay réussi.')
        } catch (hipayErr) {
          const rawMsg =
            hipayErr instanceof Error ? hipayErr.message : "Erreur lors de l'envoi HiPay."
          const hipayMsg = rawMsg.toLowerCase().includes('cannot be edited anymore') ||
            rawMsg.toLowerCase().includes('ubo statement has been submitted')
            ? "La modification des bénéficiaires n'est plus possible : la déclaration UBO a déjà été soumise à HiPay. Veuillez contacter le service client eFashion pour effectuer un changement."
            : rawMsg.replace('[UBO_CONFIRMATION_REQUIRED] ', '')
          if (rawMsg.includes('[UBO_CONFIRMATION_REQUIRED]')) {
            uboRecreateRequired.value = true
          }
          notifyError(`Données enregistrées, mais l'envoi HiPay a échoué : ${hipayMsg}`)
        }
    }
  } catch (e) {
    const message = e instanceof Error ? e.message : "Erreur lors de la sauvegarde du formulaire."
    notifyError(message)
  } finally {
    isSavingForm.value = false
    hipayBusyStep.value = null
  }
}

const openSaveBeneficiaryModal = (b: any) => {
  beneficiaryToSave.value = b
  showSaveBeneficiaryModal.value = true
}

const confirmSaveBeneficiary = async () => {
  if (!beneficiaryToSave.value) return
  await saveOneBeneficiary(beneficiaryToSave.value)
  showSaveBeneficiaryModal.value = false
  beneficiaryToSave.value = null
}

const saveOneBeneficiary = async (b: any) => {

  if (!isShareValid(b.sharePercentage)) {
    notifyError("La part de ce bénéficiaire doit être au moins de 25% pour le KYC.")
    return
  }

  const identityCard = b.identityCard as File | null
  const identityCardBack = b.identityCardBack as File | null

  if (!identityCard && !existingFiles.beneficiaries[Number(b.id)]?.frontUrl) {
    notifyError("La fiche d'identité du bénéficiaire est obligatoire pour enregistrer.")
    return
  }
  const input = {
    id: typeof b.id === 'number' ? b.id : null,
    salutation: b.salutation || null,
    lastName: b.lastName || null,
    firstName: b.firstName || null,
    identityCardNumber: b.identityCardNumber || null,
    address: b.address || null,
    postalCode: b.postalCode || null,
    city: b.city || null,
    functionInCompany: b.functionInCompany || null,
    birthDate: b.birthDate || null,
    birthCity: b.birthCity || null,
    birthCountry: b.birthCountry || null,
    nationality: b.nationality || null,
    sharePercentage: b.sharePercentage || null,
  }

  try {
    const saved = await saveVendeurHipayBeneficiary(input)
    const savedId = Number(saved.id)

    if (identityCard) {
      await uploadHipayFile(identityCard, 'beneficiary_identity_front', {
        beneficiaryId: savedId,
      })
    }
    if (identityCardBack) {
      await uploadHipayFile(identityCardBack, 'beneficiary_identity_back', {
        beneficiaryId: savedId,
      })
    }

    const idx = formData.beneficiaries.findIndex(x => x.id === b.id)
    if (idx > -1) {
      formData.beneficiaries[idx] = {
        ...formData.beneficiaries[idx],
        ...saved,
        isOpen: true,
        identityCard: null,
        identityCardBack: null,
      }
    } else {
      formData.beneficiaries.push({
        ...saved,
        isOpen: true,
        identityCard: null,
        identityCardBack: null,
      })
    }

    await refreshExistingFiles()

    success('Bénéficiaire enregistré avec succès !')
  } catch (e) {
    notifyError("Erreur lors de l'enregistrement du bénéficiaire")
  }
}

const openSaveFormModal = (step: BeuActionStep = 'all') => {
  if (isBusy.value) return
  pendingFormStep.value = step
  showSaveFormModal.value = true
}

const confirmSaveForm = async () => {
  if (isBusy.value) return
  showSaveFormModal.value = false
  await saveForm(pendingFormStep.value)
}

const openDeleteUboStatementModal = () => {
  if (isBusy.value) return
  showDeleteUboStatementModal.value = true
}

const confirmDeleteUboStatement = async () => {
  if (isBusy.value) return
  showDeleteUboStatementModal.value = false
  isDeletingUboStatement.value = true
  try {
    const result = await deleteVendeurHipayUboStatement()
    if (result?.success) {
      success(result.message || 'Déclaration UBO supprimée chez HiPay.')
    } else {
      notifyError(result?.message || 'Échec de la suppression du UBO statement.')
    }
  } catch (e) {
    notifyError(e instanceof Error ? e.message : 'Erreur lors de la suppression du UBO statement.')
  } finally {
    isDeletingUboStatement.value = false
  }
}

/**  Validation  **/
const isVatValid = (value: string) => {
  const v = (value || '').replace(/\s/g, '').toUpperCase()

  const vatRegex = /^FR[0-9A-Z]{2}[0-9]{9}$/
  return vatRegex.test(v)
}

const isIbanValid = (value: string) => {
  const v = (value || '').replace(/\s/g, '').toUpperCase()
  // IBAN FR
  // const ibanRegex = /^FR[0-9]{2}[A-Z0-9]{4}[A-Z0-9]{4}[A-Z0-9]{4}[A-Z0-9]{4}[A-Z0-9]{4}[A-Z0-9]{3}$/
  const ibanRegex = /^FR[0-9]{2}(?: ?[A-Z0-9]{4}){5} ?[A-Z0-9]{3}$/
  return v.length === 27 && ibanRegex.test(v)
}

const isPostalCodeValid = (value: string) => {
  const v = (value || '').trim()
  return /^[0-9]{5}$/.test(v)
}

const vatStatusClass = () => {
  const v = formData.company.vatNumber || ''
  if (!v) return 'border-red-500 bg-red-50'
  return isVatValid(v) ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
}

const ibanStatusClass = () => {
  const v = formData.company.iban || ''
  if (!v) return 'border-red-500 bg-red-50'
  return isIbanValid(v) ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
}

const postalStatusClass = (beneficiary: any) => {
  const v = beneficiary.postalCode || ''
  if (!v) return 'border-red-500 bg-red-50'
  return isPostalCodeValid(v) ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
}

const formatIban = (value: string) => {
  const cleanValue = value.replace(/\s/g, '')
  return cleanValue.replace(/(.{4})/g, '$1 ').trim()
}
const handlePostalCodeInput = (event: Event, beneficiary: any) => {
  const target = event.target as HTMLInputElement
  const clean = target.value.replace(/\D/g, '').slice(0, 5)
  beneficiary.postalCode = clean
}

const isShareValid = (value: string | number) => {
  const num = parseFloat(String(value ?? '').replace(',', '.'))
  return !isNaN(num) && num >= 25
}
const shareStatusClass = (beneficiary: any) => {
  const v = beneficiary.sharePercentage || ''
  if (!v) return 'border-red-500 bg-red-50'
  return isShareValid(v) ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
}



watch(
    () => formData.company.iban,
    (val) => {
      if (!val) return
      const clean = val.replace(/\s/g, '').toUpperCase().slice(0, 27)
      const formatted = formatIban(clean)
      if (formatted !== val) {
        formData.company.iban = formatted
      }
    }
)
watch(
    () => formData.company.vatNumber,
    (val) => {
      if (val == null) return
      const formatted = val.replace(/\s/g, '').toUpperCase()
      if (formatted !== val) {
        formData.company.vatNumber = formatted
      }
    }
)

// Lifecycle
onMounted(async () => {
  // Initialisation si nécessaire

  if (!authStore.isInitialized) {
    await authStore.checkAuth()
  }

  if (!authStore.isAuthenticated) {
    router.push({ name: 'auth' })
    return
  }
  await loadHipayBeu()
  // Précharger les fichiers Hipay pour afficher les fichiers existants
  // Nécessaire pour que les fichiers sauvegardés soient visibles après validation
  const shouldPreloadHipayFiles = true
  if (shouldPreloadHipayFiles) {
    // Charger les fichiers existants pour affichage après rafraîchissement
    try {
      const files = await getHipayFiles()
      // Mapper vers existingFiles structure
      existingFiles.manager.identityFrontUrl = files?.manager?.identityFront || undefined
      existingFiles.manager.identityBackUrl = files?.manager?.identityBack || undefined
      existingFiles.company.ribUrl = files?.company?.rib || undefined
      existingFiles.company.kbisUrl = files?.company?.kbis || undefined
      existingFiles.company.signedArticlesUrl = files?.company?.signedArticles || undefined
      existingFiles.beneficiaries = {}
      if (files?.beneficiaries) {
        for (const key of Object.keys(files.beneficiaries)) {
          const id = Number(key)
          existingFiles.beneficiaries[id] = {
            frontUrl: files.beneficiaries[key]?.front || undefined,
            backUrl: files.beneficiaries[key]?.back || undefined,
          }
        }
      }
    } catch (e) {
      // silently ignore listing errors
      // Erreur silencieuse lors de la récupération des fichiers
    }
  }
})

// Computed: total share of beneficiaries must not exceed 100
const totalShare = computed(() => {
  return formData.beneficiaries.reduce((sum, b) => {
    const num = parseFloat(String(b.sharePercentage || '').replace(',', '.'))
    return sum + (isNaN(num) ? 0 : num)
  }, 0)
})
const isTotalShareValid = computed(() => totalShare.value <= 100)
</script>

<style scoped>
/* Mobile-only: stack upload rows and action buttons vertically */
@media (max-width: 767px) {
  .beu-upload-row {
    flex-direction: column;
    align-items: stretch;
  }
  .beu-upload-row button {
    width: 100%;
    text-align: center;
  }
  .beu-action-buttons {
    flex-direction: column;
    gap: 0.5rem;
  }
  .beu-action-buttons .beu-btn {
    width: 100%;
    margin-right: 0;
    text-align: center;
  }
}

.accordion-enter-active,
.accordion-leave-active {
  transition: max-height 0.25s ease, opacity 0.25s ease;
}
.accordion-enter-from,
.accordion-leave-to {
  max-height: 0;
  opacity: 0;
}
.accordion-enter-to,
.accordion-leave-from {
  max-height: 2000px;
  opacity: 1;
}
</style>
